/**
 * Chat service for ClutterLogix Assistant
 * Handles API communication for chat functionality
 */
import api from './api';

const chatService = {
  /**
   * Create a new chat session
   * @param {string} title - Optional conversation title
   * @returns {Promise<Object>} Session data with session_id
   */
  createSession: async (title = null) => {
    try {
      const response = await api.post('/chat/sessions', { title });
      return response.data;
    } catch (error) {
      console.error('Error creating chat session:', error);
      throw error;
    }
  },

  /**
   * Get user's chat sessions
   * @param {number} limit - Maximum number of sessions to fetch
   * @returns {Promise<Array>} Array of conversation objects
   */
  getSessions: async (limit = 10) => {
    try {
      const response = await api.get('/chat/sessions', { params: { limit } });
      return response.data.conversations || [];
    } catch (error) {
      console.error('Error fetching chat sessions:', error);
      throw error;
    }
  },

  /**
   * Delete a chat session
   * @param {string} sessionId - Session ID to delete
   * @returns {Promise<Object>} Success response
   */
  deleteSession: async (sessionId) => {
    try {
      const response = await api.delete(`/chat/sessions/${sessionId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting chat session:', error);
      throw error;
    }
  },

  /**
   * Send a message and get AI response
   * @param {string} sessionId - Session ID
   * @param {string} message - User message
   * @returns {Promise<Object>} AI response object
   */
  sendMessage: async (sessionId, message) => {
    try {
      const response = await api.post('/chat/messages', {
        session_id: sessionId,
        message: message
      });
      return response.data;
    } catch (error) {
      console.error('Error sending chat message:', error);
      throw error;
    }
  },

  /**
   * Get messages from a session
   * @param {string} sessionId - Session ID
   * @param {number} limit - Maximum number of messages
   * @returns {Promise<Array>} Array of message objects
   */
  getMessages: async (sessionId, limit = 50) => {
    try {
      const response = await api.get(`/chat/messages/${sessionId}`, {
        params: { limit }
      });
      return response.data.messages || [];
    } catch (error) {
      console.error('Error fetching chat messages:', error);
      throw error;
    }
  },

  /**
   * Get suggested questions
   * @returns {Promise<Array>} Array of suggested question strings
   */
  getSuggestions: async () => {
    try {
      const response = await api.get('/chat/suggestions');
      return response.data.suggestions || [];
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      throw error;
    }
  }
};

export default chatService;
